import { ColumnDef } from '@tanstack/react-table';

import { Badge, BadgeColour } from '@xemplo/badge';
import { DateTimeFormat, DateTimeHelper } from '@xemplo/date-time';
import { PayrunListItem } from '@xemplo/gp-types';
import { TooltipPlacement } from '@xemplo/tooltip';

import { MoreActions } from './more-actions/more-actions';
import { PayrunNameCell } from './table-cells/payrun-name-cell';
import { formatTagsForDisplay } from './payrun-base-table.helper';
import * as S from './payrun-base-table.styles';
import { ColumnIds } from './payrun-base-table.types';
import {
  MilestoneBadgeCell,
  MilestoneDueDateCell,
  PayrunStatusCell,
} from './table-cells';

const dateTimeHelper = DateTimeHelper.with({
  format: DateTimeFormat.HumanDateMedium,
  fallback: '-',
});

export const PAYRUN_TABLE_COLUMNS: ColumnDef<PayrunListItem>[] = [
  {
    id: ColumnIds.Name,
    accessorKey: 'name',
    header: 'Name',
    size: 300,
    cell: ({ row }) => <PayrunNameCell payrun={row.original} />,
  },
  {
    id: ColumnIds.Company,
    accessorKey: ColumnIds.Company,
    header: 'Company',
    cell: ({ row }) => <S.TrunctatedText>{row.original.companyName}</S.TrunctatedText>,
  },
  {
    id: ColumnIds.BusinessUnitName,
    accessorKey: ColumnIds.BusinessUnitName,
    header: 'Business Unit',
    cell: ({ row }) => (
      <S.TrunctatedText>{row.original.businessUnitName}</S.TrunctatedText>
    ),
  },
  {
    id: ColumnIds.AmendmentDueDateTimeUtc,
    accessorKey: ColumnIds.AmendmentDueDateTimeUtc,
    header: 'Amendment Due Date',
    cell: ({ row }) => dateTimeHelper.format(row.original.amendmentDueDateTimeUtc),
  },
  {
    id: ColumnIds.AmendmentCount,
    enableSorting: false,
    accessorKey: ColumnIds.AmendmentCount,
    header: 'Amendments',
    cell: ({ row }) => (
      <Badge
        color={BadgeColour.LightWhite}
        value={row.original.amendmentCount.toString()}
      />
    ),
  },

  {
    id: ColumnIds.PayDate,
    accessorKey: ColumnIds.PayDate,
    header: 'Pay date',
    cell: ({ row }) => dateTimeHelper.format(row.original.payDate),
  },

  {
    id: ColumnIds.PayrunStatusName,
    accessorKey: ColumnIds.PayrunStatusName,
    header: 'Status',
    size: 160,
    enableSorting: false,
    cell: ({ row }) => <PayrunStatusCell {...row} />,
  },
  {
    id: ColumnIds.PayrunStatusId,
    accessorKey: ColumnIds.PayrunStatusId,
    header: 'Milestone',
    size: 150,
    cell: ({ row }) => <MilestoneBadgeCell {...row} />,
  },
  {
    id: ColumnIds.MilestoneDueDateTimeUtc,
    accessorKey: ColumnIds.MilestoneDueDateTimeUtc,
    header: 'Milestone Due Date',
    cell: ({ row }) => <MilestoneDueDateCell {...row} />,
  },
  {
    id: ColumnIds.Assignee,
    accessorKey: ColumnIds.Assignee,
    header: 'Assignee',
    size: 150,
    cell: ({ row }) => {
      const assignee = row.original.assigneeUserInfo;
      const name =
        assignee?.firstName && assignee?.lastName
          ? `${assignee.firstName} ${assignee.lastName}`
          : null;
      return <TableTooltip text={name} />;
    },
  },
  {
    id: ColumnIds.Tags,
    accessorKey: ColumnIds.Tags,
    header: 'Tags',
    enableSorting: false,
    cell: ({ row }) => {
      const tags = row.original.tags;
      return <TableTooltip text={formatTagsForDisplay(tags)} />;
    },
  },
  {
    id: ColumnIds.Actions,
    header: '',
    size: 80,
    enableSorting: false,
    cell: ({ row }) => <MoreActions payrunListItem={row.original} />,
  },
];

function TableTooltip({ text }: { text: string | number | null }) {
  if (!text) return '-';
  return (
    <S.InlineTooltip text={text.toString()} placement={TooltipPlacement.Top}>
      {text}
    </S.InlineTooltip>
  );
}
